import React, { useCallback } from 'react';
import { Grid } from '../../../shared/grid';
import { List} from 'react-virtualized';
import { validateCharacters, ValidCharacters } from 'src/store/common';

import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { Autocomplete, Chip, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import '../Download.scss';
import { useFormikContext } from 'formik';
type ScrollCtxValue ={
    scrollTopRef: React.MutableRefObject<number>;
    restoreToken: number;
};

const ScrollCtx = React.createContext<ScrollCtxValue | null>(null);

type SiteMetadataProps = {
    siteMetadataParams: any
}

interface RowProps {
    index:number,
    key: string,
    style: React.CSSProperties,
    data: any
  }

  export const VirtualizedListBoxSite = React.forwardRef<HTMLDivElement, any>(
      function ListboxComponent(props, ref) {
          const { children, selected, ...other } = props;
          const itemData = React.useMemo(() => children?.map((child: any) => child.props.value), [children]);
          const allProps = React.useMemo(() => children?.map((child: any) => child.props), [children]);
          const scrollCtx = React.useContext(ScrollCtx);
          const listRef = React.useRef<any>(null);
          const onScroll = useCallback(({ scrollTop }: { scrollTop: number }) => {
              if (scrollCtx) {
                  scrollCtx.scrollTopRef.current = scrollTop;
              }
          }, [scrollCtx]);
  
          React.useLayoutEffect(() => {
              if (!scrollCtx) return;
              if (!listRef.current) return;
              listRef.current.scrollToPosition(scrollCtx.scrollTopRef.current);
  
          }, [scrollCtx]);
  
  
          const rowRenderer = React.useCallback((data: any) => {
              return ({ index, key, style }) => {
                  return (
                      <Row index={index} key={key} style={style} data={data} />
                  )
              }
          }, []);
  
          return (
              <div ref={ref} role='button' tabIndex={0} onMouseDown={other.onMouseDown} >
                  <List
                      height={400}
                      maxHeight={400}
                      rowCount={itemData.length}
                      rowHeight={50}
                      width={665}
                      rowRenderer={rowRenderer(allProps)}
                      onScroll={onScroll}
                      {...other}
                  />
  
              </div>
          )
      }
  );
  const Row: React.FC<RowProps> = React.memo(({ index, key, style, data }) => {
    const option = data[index];
    const isSelectAll = option.value === 'select-all';
  
    // Check if it's "Select All" and if it should be checked
    const isChecked = isSelectAll
      ? data.every((opt) => opt['aria-selected'] || opt.value === 'select-all') 
      : option['aria-selected'];
      return (
      <li key={key} style={style} {...option}>
        <Checkbox
          id={key}
          color="success"
          checked={isChecked}
        />
        {option.value.display_key}
      </li>
    );
  });

export const SiteMetadata: React.FC<SiteMetadataProps> = ({siteMetadataParams}) => {
    const {values,errors, touched, setFieldTouched} = useFormikContext<any>();
    const scrollTopRef = React.useRef(0);
    const [restoreToken, setRestoreToken] = React.useState(0);

    const filterOptions = useCallback((options, state)=>{
        const inputValue = state.inputValue.trim().toLowerCase().split(/[ -]+/);
        const selectAllOpt = options[0];
        let filtered = options.filter((option:any)=>{
        const optionWords = option.display_key.trim().toLowerCase().split(/[ -]+/);
        return inputValue.every((word:string)=>{
            return optionWords.some((optionWord:string)=> optionWord.includes(word))
        })
        });
        
        if(filtered[0]!==selectAllOpt){
          filtered.unshift(selectAllOpt);
        }
        if(filtered.length!==siteMetadataParams.sitesFiltered.length){
          siteMetadataParams.setSitesFiltered(filtered);
        }
        return filtered;
      },[siteMetadataParams]);

    return (
        <div>
            <Grid row desktop={{ col: 6 }}>
                    <label>Search 
                        <Tooltip title={"Type in one of the following attributes to populate the search results. List of attributes: -Agency Landowner -State Name -Area Name -Area ID -Group Name -Site Name"} placement="top-start">
                            <InfoIcon style={{fontSize: 'medium'}}/>
                        </Tooltip>
                        <span className='required'>Required</span>
                        <div className='searchStationsDownloadPage'>
                            <i className="fas fa-search" />
                            <ScrollCtx.Provider value ={{scrollTopRef, restoreToken}}>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                noOptionsText={
                                    <div className='no-results'>
                                        <div>No results match your search criteria.</div>
                                        <div><b>Here are a few tips:</b></div>
                                        <div>• Remember to check your spelling<br/>
                                            • Ensure you are searching Site Name<br/>
                                            • Ensure you are searching Group Name<br/>
                                            • Ensure you are searching Area Name<br/>
                                        </div>
                                    </div>
                                }
                                onChange={(e, value) => {siteMetadataParams.searchSites(e, value);
                                    setRestoreToken((t)=> t+1);}}
                                value={siteMetadataParams.sitesSearched}
                                getOptionKey={(option) => option?.uniqueKey}
                                isOptionEqualToValue={(option,value)=>option.site_key===value.site_key || option.site_key===value}
                                options={(siteMetadataParams.allSites?.map((option, index) => ({
                                    ...option, 
                                    uniqueKey: `${option.site_key}-${index}`,
                                    label: `${option.site_key}`
                                })))}                               
                                renderOption={
                                    (props, option)=>(
                                    <li {...props} key={option.uniqueKey} value={option} id={option}>
                                        {option.display_key}
                                    </li>
                                )}
                                ListboxComponent={VirtualizedListBoxSite}         
                                filterOptions={filterOptions}
                                renderInput={(params) => (
                                    <TextField
                                        className="dataDownload"
                                        placeholder='Search All Sites...'
                                        onInput={
                                            (e) => {validateCharacters(e, ValidCharacters.TextField)}
                                        }
                                        aria-label={'Search Sites by Site Name-Group Name-Area Name'}
                                        data-testid={'search-bar'}
                                        {...params}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                        FormHelperTextProps={{
                                            tabIndex:0
                                        }}
                                        onBlur={()=>setFieldTouched('sites')}
                                        error={!!touched.sites && !!errors.sites}
                                    />
                                )}
                            />
                            </ScrollCtx.Provider>
                        </div>
                        <span style={{color: '#d32f2f'}}>{errors.sites && touched.sites && errors.sites}</span>
                                
                    </label>
                    {                      
                        siteMetadataParams.selectedAll ? (
                        <div className='selectedStations' style={{paddingTop: '5px', paddingBottom: '5px', fontSize: 'italics'}}>
                            <div> {`All sites have been selected. Total sites selected: ${siteMetadataParams.allSites.length-1}`}</div>
                            <div><button type='button' onClick={siteMetadataParams.clearSites}>Clear All</button></div>
                        </div>
                        ):(
                          <div className={values.sites ? 'selectedStations' : 'hideRow'}>
                          <div>{`These are your selected sites. Total sites selected: ${siteMetadataParams.currSites.length}`}</div>
                          <div className="smooth-scrollbar" style={{maxHeight: '400px', border: '1px solid #717171', width: '75vw',padding: '3px', borderRadius:'5px'}}>
                                {
                                    siteMetadataParams.currSites.map((site) =>
                                        <Chip label={`${site.display_key}`}
                                            key={site.site_key} 
                                            onDelete={() => siteMetadataParams.deleteSite(site.site_id)} 
                                            variant="outlined" 
                                            onKeyDown={(event)=>{event.key==='Backspace'&& siteMetadataParams.deleteSite(site.site_id)}}
                                            deleteIcon={<CloseIcon tabIndex={0} onKeyDown={(event) => { event.key==='Enter' && siteMetadataParams.deleteSite(site.site_id)}}/>} 
                                        />
                                    )
      
                                }
                            </div>
                            <div><button type='button' onClick={siteMetadataParams.clearSites}>Clear All</button></div>
                        </div>
                        )
                    }
                </Grid>  
        </div>
    )
}
